import {
  recognitionMetricKeys,
  type ExpExactEvidence,
  type ExpAdmission,
  type GameStateSnapshot,
  type MetricKey,
  type MetricReading,
  type RecognitionMetricKey,
  type RawReading,
  type ValidatedObservation,
} from '@/domain/types'
import type { GamePreset } from '@/presets/types'
import { GcwExpEstimator } from './gcw-exp-estimator'
import { normalizeJobName } from './job-name'
import { ExpAdmissionFilter, type ExpAdmissionCheckpoint } from './exp-admission'
import { SourceContinuity } from './source-continuity'
import {
  enrichExpRequirement,
  expMatchesRequirement,
  ExpRequirementEstimator,
} from './exp-requirement-estimator'

type PreviousReadings = { -readonly [Key in RecognitionMetricKey]?: RawReading[Key] }

interface TrustedExpEvidencePoint {
  readonly level: number
  readonly absolute: number
  readonly ratio: number
  readonly requiredExp: number | null
  readonly sourceAt: number
}

interface ExactEvidenceDecision {
  readonly accepted: boolean
  readonly evidence?: ExpExactEvidence
}

export class ReadingValidator {
  private readonly gcwExp = new GcwExpEstimator()
  private percentageMode = false
  private readonly expAdmission = new ExpAdmissionFilter()
  private sourceIds: RawReading['metricSourceIds'] = {}
  private previous: PreviousReadings = {}
  private pendingLevelIncrease: { from: number; to: number; capturedAt: number } | null = null
  private trustedExactExp: TrustedExpEvidencePoint | null = null
  private trustedAdmissionEpoch = 0
  private readonly expRequirements = new ExpRequirementEstimator()
  private readonly mesosPresence = new SourceContinuity()
  private mesosSamples: MetricReading<number>[] = []
  private mesosLastSourceAt: number | null = null
  private mesosLastFrameAt = -Infinity
  private mesosContinuityId: number | null = null
  private confirmedExpRebaseline = false
  private confirmedExactExpRebaseline = false

  constructor(
    private readonly preset: GamePreset,
    private minimumConfidence = 0.62,
  ) {}

  setMinimumConfidence(value: number): void {
    this.minimumConfidence = value
  }

  resetMetricContinuity(metrics: readonly MetricKey[]): void {
    if (metrics.includes('exp')) this.resetExpContinuity()
    for (const metric of metrics) delete this.previous[metric]
    if (metrics.includes('mesos')) {
      this.mesosPresence.reset()
      this.mesosSamples = []
      this.mesosLastSourceAt = null
      this.mesosLastFrameAt = -Infinity
      this.mesosContinuityId = null
    }
  }

  resetExpContinuity(): void {
    this.expAdmission.breakContinuity()
    this.gcwExp.breakContinuity()
  }

  expCheckpoint(): ExpAdmissionCheckpoint {
    return { ...this.expAdmission.checkpoint(), ...(this.preset.id === 'gcw' ? { gcwRequirements: this.gcwExp.checkpoint() } : {}) }
  }

  restoreExpCheckpoint(checkpoint: ExpAdmissionCheckpoint): void {
    this.expAdmission.restore(checkpoint)
    this.gcwExp.restore(checkpoint.gcwRequirements)
    this.trustedAdmissionEpoch = checkpoint.epoch
    const anchor = checkpoint.anchor
    this.trustedExactExp = anchor && anchor.level !== null ? {
      level: anchor.level, absolute: anchor.value.absolute!, ratio: anchor.value.ratio!,
      requiredExp: anchor.value.requiredExp ?? null, sourceAt: anchor.sourceAt,
    } : null
    this.expRequirements.reset(anchor?.level != null ? { level: anchor.level, value: anchor.value } : null)
  }

  validate(reading: RawReading): ValidatedObservation {
    const percentageMode = this.preset.id === 'gcw' && reading.expPercentCandidate !== undefined
    if (percentageMode !== this.percentageMode) {
      this.expAdmission.reset()
      this.trustedExactExp = null
      this.percentageMode = percentageMode
    }
    if (percentageMode) reading = { ...reading, expExactCandidate: undefined, expExactEvidence: undefined }
    const mesosFrameInOrder = Number.isFinite(reading.capturedAt) && reading.capturedAt >= this.mesosLastFrameAt
    for (const metric of recognitionMetricKeys) {
      if (metric === 'mesos' && !mesosFrameInOrder) continue
      const previous = this.sourceIds?.[metric], next = reading.metricSourceIds?.[metric]
      if (previous !== undefined && previous !== next) {
        if (metric === 'exp' || metric === 'level') this.resetExpContinuity()
        delete this.previous[metric]
        if (metric === 'mesos') this.resetMetricContinuity(['mesos'])
        if (metric === 'level') this.pendingLevelIncrease = null
      }
    }
    this.sourceIds = { ...reading.metricSourceIds, ...(!mesosFrameInOrder ? { mesos: this.sourceIds?.mesos } : {}) }
    this.confirmedExpRebaseline = false
    this.confirmedExactExpRebaseline = false
    if (this.pendingLevelIncrease && reading.capturedAt - this.pendingLevelIncrease.capturedAt > 15_000) {
      this.pendingLevelIncrease = null
    }
    const previousLevel = this.previous.level?.value
    const previousExpRatio = this.previous.exp?.value?.ratio
    const acceptedLevel = this.acceptMetric('level', reading)
    if (acceptedLevel && reading.level.value !== null) {
      this.expAdmission.observeLevel(reading.level.value, reading.level.capturedAt, reading.capturedAt)
    }
    const decisions = new Map<number, ExpAdmission>()
    const samples = [reading.expExactEvidence, reading.expExactCandidate
      ? { ...reading.expExactCandidate, confirmation: 'source-frame' as const } : undefined]
      .filter((sample): sample is ExpExactEvidence => sample !== undefined)
      .map(sample => ({ ...sample, levelSourceAt: acceptedLevel && sample.level === reading.level.value
        ? sample.levelSourceAt === undefined ? reading.level.capturedAt
          : sample.levelSourceAt === null ? null : Math.min(sample.levelSourceAt, reading.level.capturedAt)
        : null }))
      .sort((a, b) => a.sourceAt - b.sourceAt)
    for (const sample of samples) decisions.set(sample.sourceAt, this.expAdmission.observe(sample, this.minimumConfidence))
    const resolved = this.expAdmission.resolvePending(reading.capturedAt, this.minimumConfidence)
    if (resolved) {
      decisions.set(resolved.evidence.sourceAt, resolved.admission)
      reading = { ...reading, expExactEvidence: resolved.evidence,
        exp: { ...reading.exp, value: resolved.evidence.value, capturedAt: resolved.evidence.sourceAt,
          confidence: resolved.evidence.confidence, source: 'ocr', availability: 'available' } }
    } else if (reading.expExactEvidence) {
      const normalized = samples.find(sample => sample.sourceAt === reading.expExactEvidence!.sourceAt)
      if (normalized) reading = { ...reading, expExactEvidence: normalized }
    }
    const candidateAdmission = reading.expExactCandidate ? decisions.get(reading.expExactCandidate.sourceAt) : undefined
    const evidenceAdmission = reading.expExactEvidence ? decisions.get(reading.expExactEvidence.sourceAt) : undefined
    if (reading.expExactCandidate && candidateAdmission?.level != null) {
      reading = { ...reading, expExactCandidate: { ...reading.expExactCandidate, level: candidateAdmission.level } }
    }
    const exactDecision = evidenceAdmission?.status === 'accepted'
      ? this.acceptExpExactEvidence({ ...reading.expExactEvidence!, value: evidenceAdmission.value!,
          level: evidenceAdmission.level ?? reading.expExactEvidence!.level }, evidenceAdmission)
      : { accepted: false }
    const mesos = this.confirmMesos(reading)
    let preparedReading = this.withObservedExpRequirement({
      ...reading,
      ...(mesos ? { mesos } : {}),
      ...(typeof reading.jobName?.value === 'string'
        ? { jobName: { ...reading.jobName, value: (this.preset.normalizeJobName ?? normalizeJobName)(reading.jobName.value) } }
        : {}),
    }, exactDecision.evidence)
    const accepted = Object.fromEntries(recognitionMetricKeys.map((key) => [key, this.acceptMetric(key, preparedReading)])) as Record<RecognitionMetricKey, boolean>
    accepted.mesos = mesos !== null && accepted.mesos
    const currentAdmission = resolved?.admission ?? (samples.length ? decisions.get(samples.at(-1)!.sourceAt) : undefined)
    const estimated = reading.exp.value && (reading.exp.value.estimated || reading.exp.value.absolute === null)
    let expQuarantined = this.expAdmission.isPending() || (currentAdmission !== undefined && currentAdmission.status !== 'accepted')
      || Boolean(estimated && !this.expAdmission.permitsEstimate(reading.level.value, reading.exp.value!.ratio, reading.exp.capturedAt))
    if (expQuarantined) accepted.exp = false
    if (exactDecision.evidence && exactDecision.evidence.level !== preparedReading.level.value) accepted.exp = false
    if (this.preset.id === 'gcw') for (const sample of samples) {
      const admission = decisions.get(sample.sourceAt)
      if (admission?.status === 'accepted' && admission.value) this.gcwExp.observeExact({ ...sample, value: admission.value, level: admission.level ?? sample.level })
    }
    const percent = percentageMode ? this.gcwExp.observe({ ...reading.expPercentCandidate!,
      level: accepted.level && reading.level.value === reading.expPercentCandidate!.level ? reading.level.value : null,
      deliveredAt: reading.capturedAt }, this.minimumConfidence, Math.max(15_000, (reading.sourceObservation?.samplingIntervalMs ?? 0) * 3)) : undefined
    if (percent) {
      accepted.exp = percent.admission.status === 'accepted'
      expQuarantined = !accepted.exp
      if (percent.evidence) preparedReading = { ...preparedReading, exp: { ...reading.exp, value: percent.evidence.value,
        availability: 'available', confirmation: 'consensus', capturedAt: percent.evidence.sourceAt } }
    }
    const observation: ValidatedObservation = {
      ...preparedReading,
      accepted,
      expCandidateAdmission: candidateAdmission,
      expEvidenceAdmission: evidenceAdmission,
      expQuarantined,
      ...(percent ? { expEstimatedEvidence: percent.evidence, expEstimatedAdmission: percent.admission } : {}),
      ...(reading.expExactEvidence ? { expExactEvidenceAccepted: exactDecision.accepted } : {}),
      ...(this.confirmedExpRebaseline ? { expRebaseline: true as const } : {}),
      ...(this.confirmedExactExpRebaseline ? { expExactRebaseline: true as const } : {}),
    }
    if (accepted.level && typeof previousLevel === 'number' && preparedReading.level.value === previousLevel + 1) {
      this.pendingLevelIncrease = { from: previousLevel, to: preparedReading.level.value, capturedAt: preparedReading.capturedAt }
    }
    if (accepted.exp && typeof previousExpRatio === 'number' && preparedReading.exp.value?.ratio !== null && preparedReading.exp.value!.ratio! < previousExpRatio - 0.08) {
      this.pendingLevelIncrease = null
    }
    if (observation.expRebaseline) this.pendingLevelIncrease = null
    if (accepted.level) this.previous.level = preparedReading.level
    if (accepted.exp) this.previous.exp = preparedReading.exp
    if (accepted.hp) this.previous.hp = preparedReading.hp
    if (accepted.mp) this.previous.mp = preparedReading.mp
    if (accepted.mesos) {
      this.previous.mesos = preparedReading.mesos
    }
    if (accepted.mapName) this.previous.mapName = preparedReading.mapName
    if (accepted.jobName) this.previous.jobName = preparedReading.jobName
    return observation
  }

  resetBaseline(state: GameStateSnapshot | null = null): void {
    this.gcwExp.reset()
    this.percentageMode = state?.exp.percentageOnly === true
    if (state && state.level !== null) this.gcwExp.seed({ level: state.level, value: state.exp,
      levelSourceAt: state.observedAt?.level, sourceAt: state.observedAt?.exp ?? state.capturedAt,
      deliveredAt: state.capturedAt, confidence: state.confidence.exp, confirmation: 'consensus' })
    this.sourceIds = {}
    this.expAdmission.reset(state)
    this.trustedAdmissionEpoch = 0
    this.previous = state ? {
      ...(state.level === null ? {} : { level: this.seedReading('level', state.level, state) }),
      ...(state.exp.absolute === null && state.exp.ratio === null ? {} : { exp: this.seedReading('exp', state.exp, state) }),
      ...(state.hp.current === null && state.hp.ratio === null ? {} : { hp: this.seedReading('hp', state.hp, state) }),
      ...(state.mp.current === null && state.mp.ratio === null ? {} : { mp: this.seedReading('mp', state.mp, state) }),
      ...(state.mesos === null ? {} : { mesos: this.seedReading('mesos', state.mesos, state) }),
      ...(state.mapName === null ? {} : { mapName: this.seedReading('mapName', state.mapName, state) }),
    } : {}
    this.pendingLevelIncrease = null
    this.trustedExactExp = state ? this.exactEvidencePoint(state) : null
    this.expRequirements.reset(state?.level === null || state?.level === undefined
      ? null
      : { level: state.level, value: state.exp })
    this.mesosPresence.reset()
    this.mesosSamples = []
    this.mesosLastSourceAt = this.previous.mesos?.capturedAt ?? null
    this.mesosLastFrameAt = -Infinity
    this.mesosContinuityId = null
  }

  private confirmMesos(reading: RawReading): MetricReading<number> | null {
    if (this.preset.capabilities.mesos === 'unavailable'
      || !Number.isFinite(reading.capturedAt) || reading.capturedAt < this.mesosLastFrameAt) return null
    const identity = reading.mesosContinuityId
    if (identity !== undefined && (!Number.isSafeInteger(identity) || identity < 0
      || this.mesosContinuityId !== null && identity < this.mesosContinuityId)) return null
    this.mesosLastFrameAt = reading.capturedAt
    const broken = this.mesosPresence.observe(reading)
    const identityChanged = identity !== undefined && identity !== this.mesosContinuityId
    if (identity !== undefined) this.mesosContinuityId = identity
    const hidden = this.mesosPresence.hasEvidence && this.mesosPresence.startAt('mesos') === null
    const unavailable = !this.mesosPresence.hasEvidence && reading.mesos.availability === 'unavailable'
    if (broken.mesos || identityChanged || hidden || unavailable) {
      this.mesosSamples = []
      this.mesosLastSourceAt = null
      delete this.previous.mesos
    }
    if (hidden || unavailable) return null

    const sample = reading.mesos
    if (sample.availability === 'unavailable' || sample.value === null
      || !Number.isSafeInteger(sample.value) || sample.value < 0
      || !Number.isFinite(sample.confidence) || sample.confidence < this.minimumConfidence
      || sample.confidence > 1 || !Number.isFinite(sample.capturedAt) || sample.capturedAt > reading.capturedAt
      || this.mesosLastSourceAt !== null && sample.capturedAt <= this.mesosLastSourceAt
      || this.mesosPresence.hasEvidence && !this.mesosPresence.contains('mesos', sample.capturedAt)) return null
    if (!this.mesosPresence.hasEvidence && this.mesosLastSourceAt !== null
      && sample.capturedAt - this.mesosLastSourceAt > 15_000) {
      this.mesosSamples = []
      delete this.previous.mesos
    }
    this.mesosLastSourceAt = sample.capturedAt
    this.mesosSamples.push({ ...sample })
    if (this.mesosSamples.length > 3) this.mesosSamples.shift()
    if (this.mesosSamples.length < 3) return null
    // Select an observed endpoint, preserving its time rather than smoothing a balance at delivery time.
    const median = [...this.mesosSamples].sort((a, b) => a.value! - b.value!)[1]!
    const confirmed = this.mesosSamples.filter(candidate => candidate.value === median.value).at(-1)!
    if (this.previous.mesos && confirmed.capturedAt <= this.previous.mesos.capturedAt) return null
    return { ...confirmed, confirmation: 'consensus' }
  }

  private acceptExpExactEvidence(evidence: ExpExactEvidence | undefined, admission?: ExpAdmission): ExactEvidenceDecision {
    if (!evidence || admission?.status !== 'accepted' || evidence.level === null
      || evidence.value.absolute === null || evidence.value.ratio === null) return { accepted: false }
    const previous = this.trustedExactExp
    const revision = admission.change?.kind === 'level-up' && admission.change.revision > 1
      && previous?.sourceAt === evidence.sourceAt && previous.level !== evidence.level
    if (previous && evidence.sourceAt <= previous.sourceAt && !revision) return { accepted: false }
    this.expRequirements.seed(evidence.level, evidence.value)
    const point: TrustedExpEvidencePoint = {
      level: evidence.level, absolute: evidence.value.absolute, ratio: evidence.value.ratio,
      requiredExp: evidence.value.requiredExp ?? null, sourceAt: evidence.sourceAt,
    }
    const replacement = admission.rebaseline && (!previous || previous.sourceAt < admission.rebaseline.sourceAt)
    const epochChanged = admission.accounting !== undefined && admission.accounting.epoch !== this.trustedAdmissionEpoch
    if (admission.accounting) this.trustedAdmissionEpoch = admission.accounting.epoch
    const missingLevels = previous && (point.level < previous.level || point.level > previous.level + 1)
    if (replacement || missingLevels || epochChanged) {
      this.confirmedExactExpRebaseline = true
      this.confirmedExpRebaseline = true
    }
    this.trustedExactExp = point
    return { accepted: true, evidence }
  }

  private exactEvidencePoint(state: GameStateSnapshot): TrustedExpEvidencePoint | null {
    if (state.level === null || state.exp.absolute === null || state.exp.ratio === null || state.exp.estimated) return null
    const required = state.exp.requiredExp ?? null
    if (state.exp.absolute < 0
      || state.exp.ratio < 0
      || state.exp.ratio > 1
      || !expMatchesRequirement(state.exp, required)) {
      return null
    }
    return {
      level: state.level,
      absolute: state.exp.absolute,
      ratio: state.exp.ratio,
      requiredExp: required,
      sourceAt: state.observedAt?.exp ?? state.capturedAt,
    }
  }

  private withObservedExpRequirement(reading: RawReading, evidence?: ExpExactEvidence): RawReading {
    const level = evidence?.level ?? (typeof reading.level.value === 'number' ? reading.level.value : null)
    const value = reading.exp.value
    if (level === null || value === null) {
      return evidence ? { ...reading, expExactEvidence: evidence } : reading
    }
    const observed = evidence?.value.requiredExp
    const known = observed !== undefined ? observed : this.expRequirements.requirementFor(level)
    if (known === undefined) return evidence ? { ...reading, expExactEvidence: evidence } : reading
    const exp = {
      ...reading.exp,
      value: enrichExpRequirement({ ...value,
        requiredExpRange: evidence?.value.requiredExpRange ?? value.requiredExpRange,
        percentagePrecision: evidence?.value.percentagePrecision ?? value.percentagePrecision,
      }, known, known !== null),
    }
    return { ...reading, exp, ...(evidence ? { expExactEvidence: evidence } : {}) }
  }

  private seedReading<Key extends MetricKey>(metric: Key, value: NonNullable<RawReading[Key]['value']>, state: GameStateSnapshot): MetricReading<typeof value> {
    return {
      value,
      confidence: state.confidence[metric],
      source: this.preset.capabilities[metric] === 'ocr' ? 'ocr' : 'fused',
      availability: 'available',
      capturedAt: state.observedAt?.[metric] ?? state.capturedAt,
    }
  }

  private acceptMetric(metric: RecognitionMetricKey, reading: RawReading): boolean {
    if (this.preset.capabilities[metric] === 'unavailable') return false

    if (metric === 'level') {
      const candidate = reading.level
      const previous = this.previous.level
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      if (!Number.isFinite(candidate.capturedAt) || candidate.capturedAt > reading.capturedAt
        || previous && (candidate.capturedAt < previous.capturedAt
          || candidate.capturedAt === previous.capturedAt && candidate.value !== previous.value)) return false
      const value = candidate.value as number
      if (!Number.isInteger(value) || value < 1 || value > 200) return false
      if (previous?.value === null || previous?.value === undefined) return candidate.confirmation === 'consensus'
      if (value === previous.value) return true
      return candidate.confirmation === 'consensus'
    }

    if (metric === 'mesos') {
      const candidate = reading.mesos
      return candidate.confirmation === 'consensus'
    }

    if (metric === 'mapName') {
      const candidate = reading.mapName
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      const value = candidate.value.trim()
      return value.length >= 2 && value.length <= 32 && /[\u3400-\u9fffA-Za-z0-9]/.test(value)
    }

    if (metric === 'jobName') {
      const candidate = reading.jobName
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      return (this.preset.normalizeJobName ?? normalizeJobName)(candidate.value) !== null
    }

    if (metric === 'exp') {
      const candidate = reading.exp
      const previous = this.previous.exp
      if (previous && candidate.capturedAt <= previous.capturedAt) return false
      if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
      const value = candidate.value
      if (value.ratio !== null && (value.ratio < 0 || value.ratio > 1)) return false
      if (value.absolute !== null && value.absolute < 0) return false
      const level = reading.level.value
      const simultaneousLevelUp = typeof level === 'number'
        && typeof this.previous.level?.value === 'number'
        && level === this.previous.level.value + 1
      const pendingLevelUp = this.pendingLevelIncrease !== null
        && level === this.pendingLevelIncrease.to
        && reading.capturedAt - this.pendingLevelIncrease.capturedAt <= 15_000
      const levelUpSupported = simultaneousLevelUp || pendingLevelUp
      if (value.absolute !== null && value.ratio !== null) {
        const zeroEndpoint = value.absolute === 0 && value.ratio === 0
        const exactIsConfirmed = zeroEndpoint
          ? levelUpSupported || value.requiredExp !== undefined
          : value.requiredExp !== undefined && expMatchesRequirement(value, value.requiredExp ?? null)
        if (!exactIsConfirmed) return false
      }
      const previousRatio = previous?.value?.ratio
      const previousAbsolute = previous?.value?.absolute
      const ratioReset = typeof previousRatio === 'number'
        && value.ratio !== null
        && (value.ratio < previousRatio - 0.018 || (value.ratio <= 0.001 && previousRatio > 0.001))
      const absoluteReset = typeof previousAbsolute === 'number'
        && value.absolute !== null
        && value.absolute < previousAbsolute
      if (ratioReset || absoluteReset) {
        if (!levelUpSupported && !this.confirmedExactExpRebaseline) return false
        if (!levelUpSupported) this.confirmedExpRebaseline = true
      }
      if (candidate.source === 'ocr' && reading.diagnostics.metrics?.exp?.status === 'confirming') return false
      return true
    }

    const candidate = metric === 'hp' ? reading.hp : reading.mp
    if (candidate.availability === 'unavailable' || candidate.value === null || candidate.confidence < this.minimumConfidence) return false
    const value = candidate.value
    if (value.ratio !== null && (value.ratio < 0 || value.ratio > 1)) return false
    if (value.current !== null && value.current < 0) return false
    if (value.max !== null && (value.max <= 0 || (value.current !== null && value.current > value.max * 1.05))) return false
    return true
  }

}
